import type { Timestamp } from 'firebase/firestore'

// Listing type enum
export type ListingType = 'general' | 'vehicle' | 'fundraiser'

// Sale type
export type SaleType = 'buynow' | 'auction' | 'product'

// Listing status
export type ListingStatus = 'active' | 'sold' | 'expired' | 'unsold' | 'closed' | 'draft' | 'deleted'

// Item condition
export type Condition = 'new' | 'excellent' | 'good' | 'fair' | 'used'

// Fundraiser type
export type FundraiserType = 'auction' | 'products' | 'raffle' | 'sponsorship'

// Vehicle transmission
export type Transmission = 'Automatic' | 'Manual'

// Fuel type
export type FuelType = 'Petrol' | 'Diesel' | 'Electric' | 'Hybrid' | 'LPG'

// Base listing interface (all listings have these fields)
export interface BaseListing {
  id: string
  title: string
  description: string
  price: number | null
  wasPrice?: number // Original/previous price for discounted items
  priceDisplay?: string // e.g., "$550/week", "Price by Negotiation"
  images: string[]
  category: string
  listingType: ListingType
  saleType?: SaleType
  condition?: Condition
  brand?: string // Brand name for products
  location: string
  suburb?: string

  // Shipping & Payment
  shippingOptions?: string[] // ['Pickup', 'Courier', 'Post']
  paymentOptions?: string[] // ['Cash', 'Bank Transfer', 'Card']

  // Seller info
  sellerId: string
  sellerName: string
  sellerRating?: number
  sellerReviews?: number
  sellerVerified?: boolean
  sellerBusiness?: boolean

  // Agent info (dealership listings)
  agentName?: string
  agentRole?: string
  agentPhotoUrl?: string
  agentEmail?: string
  agentPhone?: string

  // E-commerce (storefront/nonprofit inventory)
  stock?: number           // Available quantity (undefined = classifieds mode)
  sold?: number            // Total units sold
  sku?: string             // Optional seller-assigned SKU

  // Buyer-flow toggles (independent of saleType)
  enableOffers?: boolean   // Show "Make Offer" button to buyers

  // Finance
  financeOptIn?: boolean   // Per-listing Good Lending opt-in (used when tier mode is 'per-listing')

  // Metadata
  status: ListingStatus
  views: number
  createdAt: Timestamp
  updatedAt?: Timestamp
  expiresAt?: Timestamp
}

// Auction fields (added when saleType === 'auction')
export interface AuctionFields {
  saleType: 'auction'
  startingPrice: number
  currentBid?: number
  bidCount: number
  highBidderId?: string
  highBidderName?: string
  reservePrice?: number
  reserveMet?: boolean
  buyNowPrice?: number // Optional instant-purchase price that ends the auction
  // Auto-bid (proxy bidding)
  maxBid?: number
  maxBidderId?: string
  maxBidderName?: string
  lastBidAt?: Timestamp
  auctionEnded?: boolean
}

// Vehicle-specific fields
export interface VehicleFields {
  listingType: 'vehicle'
  make: string
  model: string
  year: number
  odometer: number
  colour: string
  bodyType?: string
  doors?: number
  engineCC?: number
  fuelType?: FuelType
  transmission?: Transmission
  previousOwners?: string | number
  wofExpiry?: string
  regoExpiry?: string
  stolenCheck?: 'Passed' | 'Failed' | 'Pending'
  damageCheck?: 'Passed' | 'Failed' | 'Pending'

  // Added 2026-05-25 to mirror the legacy iSQROLL automotive form.
  // All optional so existing listings keep validating.
  modelDetail?: string                 // Sub-variant e.g. "GTi", "Sport Hybrid"
  importHistory?: string               // "NZ New", "Used Import - Japan", etc.
  seats?: number
  cylinders?: number
  plateOrVin?: string                  // Persisted on the listing (was lookup-only)
  reportedStolen?: boolean             // Seller declaration; CarJam pre-fills if flagged
  importedDamaged?: boolean            // Seller declaration; CarJam pre-fills if flagged
  onRoadCostsIncluded?: boolean        // WoF / rego / RUC included in the sale price

  // Dealer import tracking (set by Cloud Functions)
  importSource?: 'autoplay' | 'motorcentral'
  importSourceId?: string
  importedAt?: Timestamp
}

// Fundraiser product
export interface FundraiserProduct {
  id: string
  name: string
  price: number
  description?: string
  image?: string
  stock?: number
  sold?: number
}

// Raffle prize
export interface RafflePrize {
  id: string
  name: string
  description?: string
  value?: number
  image?: string
}

// Sponsorship tier
export interface SponsorshipTier {
  id: string
  name: string
  amount: number
  description: string
  benefits: string[]
  limit?: number
  claimed?: number
}

// Fundraiser-specific fields
export interface FundraiserFields {
  listingType: 'fundraiser'
  fundraiserType: FundraiserType
  organizationName?: string
  organizationLogo?: string
  closingDate?: string
  targetAmount?: number
  amountRaised?: number
  backerCount?: number

  // Auction-specific
  bidIncrement?: number

  // Products
  products?: FundraiserProduct[]

  // Raffle
  ticketPrice?: number
  totalTickets?: number
  ticketsSold?: number
  maxTicketsPerPerson?: number
  prizes?: RafflePrize[]
  termsAndConditions?: string

  // Sponsorship
  tiers?: SponsorshipTier[]
  sponsorRecognition?: string

  // Payment & shipping
  deliveryOptions?: string[]
  paymentOptions?: string[]
  shippingOptions?: string[]
  shippingCost?: number
  // Per-method shipping rates set by the seller. NZD, GST-inclusive.
  // Pickup is always free; missing methods block checkout for that method.
  shippingCostCourier?: number
  shippingCostPost?: number
}

// Combined listing types
export type GeneralListing = BaseListing & { listingType: 'general' }
export type VehicleListing = BaseListing & VehicleFields
export type FundraiserListing = BaseListing & FundraiserFields
export type AuctionListing = BaseListing & AuctionFields

// Union type for all listings
export type Listing = GeneralListing | VehicleListing | FundraiserListing | (BaseListing & AuctionFields)

// Type guards
export function isVehicleListing(listing: Listing): listing is VehicleListing {
  return listing.listingType === 'vehicle'
}

export function isFundraiserListing(listing: Listing): listing is FundraiserListing {
  return listing.listingType === 'fundraiser'
}

export function isAuctionListing(listing: Listing): listing is AuctionListing {
  return listing.saleType === 'auction'
}

// Whether a listing accepts buyer offers. Explicit `enableOffers` wins; otherwise
// falls back to the legacy default (fixed-price non-product non-fundraiser).
export function listingAcceptsOffers(listing: Listing): boolean {
  if (listing.enableOffers !== undefined) return listing.enableOffers
  if (isFundraiserListing(listing)) return false
  if (listing.saleType === 'product') return false
  // Auctions historically had no Make Offer; keep that as the legacy default
  if (isAuctionListing(listing)) return false
  return true
}

// Effective non-card payment options for a listing's checkout, given the
// seller's tier. Dealerships are enquiry-only and accept none. General accounts
// implicitly accept Cash on Pickup even when the listing stored no explicit
// options (legacy listings, or the seller unchecked everything) — it requires
// zero setup, so a general seller should never read as "payments not set up".
export function effectivePaymentOptions(
  paymentOptions: string[] | undefined,
  sellerTier: string | undefined,
): string[] {
  if (sellerTier === 'dealership') return []
  const opts = paymentOptions || []
  if (opts.length > 0) return opts
  if (!sellerTier || sellerTier === 'general') return ['Cash on Pickup']
  return opts
}

// Whether a listing can be purchased instantly via "Buy Now".
// Buy Now is available on fixed-price listings, and on auctions that opted in
// with a `buyNowPrice`.
export function listingHasBuyNow(listing: Listing): boolean {
  if (isFundraiserListing(listing)) return false
  if (listing.saleType === 'product') return false // Product mode uses cart
  if (isAuctionListing(listing)) {
    return !!(listing as Listing & { buyNowPrice?: number }).buyNowPrice
  }
  return !!listing.price && listing.price > 0
}

// Defensive millis extraction — tolerates a Firestore Timestamp (.toMillis()),
// a raw number, or a Date. Returns undefined if the value isn't a recognizable
// timestamp so the caller can skip the check.
function toMillisLoose(value: unknown): number | undefined {
  if (value == null) return undefined
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis()
  }
  return undefined
}

// Whether the auction "Buy Now" (buy-out) option should be offered. Buy-out is
// only available before the first bid (Trade Me style), on a live, unexpired
// auction with a positive buyNowPrice, and never to the owner.
export function canBuyOutAuction(listing: Listing, isOwner: boolean, nowMs: number = Date.now()): boolean {
  if (isOwner) return false
  if (listing.saleType !== 'auction') return false
  if (listing.status !== 'active') return false
  const l = listing as Listing & { buyNowPrice?: number; auctionEnded?: boolean; bidCount?: number; expiresAt?: unknown }
  if (!l.buyNowPrice || l.buyNowPrice <= 0) return false
  if (l.auctionEnded) return false
  if (l.bidCount && l.bidCount > 0) return false
  const expiresAtMs = toMillisLoose(l.expiresAt)
  if (expiresAtMs !== undefined && expiresAtMs < nowMs) return false
  return true
}

// Form data types (for creating/editing)
export interface CreateListingData {
  title: string
  description: string
  price?: number
  category: string
  listingType: ListingType
  saleType?: SaleType
  condition?: Condition
  location: string
  suburb?: string
  images: (File | string)[]
  // Auction fields
  auctionDuration?: number
  reservePrice?: number
  buyNowPrice?: number
  startingPrice?: number
  currentBid?: number
  bidCount?: number
  reserveMet?: boolean
  expiresAt?: Date
  // Buyer-flow toggles
  enableOffers?: boolean
  [key: string]: unknown // Additional type-specific fields
}
